import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
// Hooks
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import employeesCollection from 'modules/employees/store/collections/employees'
import AllowanceMapper from 'modules/timeoff/components/TimeoffAllowances/AllowanceMapper'
import PolicyForm from 'modules/timeoff/lib/TimeoffPolicy/Select'
import { timeoffPolicyEmployees } from 'modules/timeoff/lib/timeoffPolicyEmployees'
import allowanceIncidences, {
  employeeAllowanceIncidences,
} from 'modules/timeoff/store/collections/allowanceIncidences'
import allowances from 'modules/timeoff/store/collections/allowances'
import timeoffPolicies, {
  AllowanceMapItem,
  BulkAssignEmployeesDto,
  employeePolicy,
} from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import type TimeoffPolicy from 'modules/timeoff/store/models/TimeoffPolicy/Policy'

interface Props {
  policyId: number
  onClose: () => void
}

const RemovePolicy = ({ policyId, onClose }: Props) => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const policyForm = React.useMemo(() => new PolicyForm({}), [])
  const [showSelectors, setShowSelectors] = React.useState(true)
  const [showAdjustmentLossAlert, setShowAdjustmentLossAlert] = React.useState<boolean>(false)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)

  const { loading } = useAsyncData(async () => {
    await Promise.all([timeoffPolicies.fetch(), allowances.fetch()])
    if (!timeoffPolicies.length) {
      onClose()
    }
    await allowanceIncidences.fetch({
      data: {
        timeoff_allowance_ids: allowances
          .filter((a) => a.attributes.timeoff_policy_id == policyId)
          .map((a) => a.id),
      },
    })
  }, [])

  const policies = timeoffPolicies.models

  const policy = React.useMemo<TimeoffPolicy | null | undefined>(() => {
    if (loading) return null

    const timeoffPolicy = policies.find((p) => p.attributes.id === policyId)

    if (!timeoffPolicy) {
      throw Error(`Error getting timeoffPolicies with id = ${policyId}`)
    }

    return timeoffPolicy
  }, [policies, policyId, loading])

  const employees = policy ? timeoffPolicyEmployees(policy, employeesCollection) : []
  const hasEmployees = employees.length > 0

  const options = React.useMemo<SelectOption[]>(
    () =>
      policies
        .filter((top) => top.attributes.id !== policy?.attributes.id)
        .map((top) => ({
          label: top.attributes.name,
          value: top.attributes.id,
        })),
    [policies, policy]
  )

  const description = showAdjustmentLossAlert
    ? i18n.t('settings.timeoff_policies.adjustment_loss_modal.description')
    : i18n.t('settings.timeoff_policies.move_employee_modal.description_complete')

  const employeeIds = employees.map((employee) => employee.id)
  const selectedPolicyId = policyForm.get('timeoff_policy_id').value

  const onSelect = (field: Field) => {
    const selectedPolicy = timeoffPolicies.find({
      id: +field.value,
    })
    if (hasAllowancesWithIncidences) {
      setBulkAssignEmployeeDto({
        employeeIds: employeeIds,
        mappings: allowanceMappings,
        policyId: +field.value,
      })
    }

    const defaultAllowanceId = selectedPolicy?.attributes.default_allowance_id

    if (!defaultAllowanceId) return

    policyForm.get('timeoff_allowance_id').set(defaultAllowanceId)
  }

  const allowanceId = policyForm.get('timeoff_allowance_id').value

  const [currentAllowances, setCurrentAllowances] = React.useState<Allowance[]>([])

  useEffect(() => {
    const innerAllowances: Allowance[] = []

    employees.forEach((employee) => {
      if (employee) {
        employeePolicy(employee).allowances.forEach((allowance) => {
          if (!innerAllowances.includes(allowance)) {
            innerAllowances.push(allowance)
          }
        })
      }
    })

    setCurrentAllowances(innerAllowances)
  }, [employees.length])

  const incidenceAllowanceIdsPerEmployee = () => {
    const allAllowanceIds: number[] = []
    employees.map((employee) => {
      const employeeIncidences = employeeAllowanceIncidences(employee)
      const employeeIncidenceIds = employeeIncidences.map(
        (incidence) => incidence!.attributes.timeoff_allowance_id
      )
      allAllowanceIds.push(...employeeIncidenceIds)
    })
    return allAllowanceIds
  }

  const allowancesWithIncidences = currentAllowances!.filter((allowance) => {
    return incidenceAllowanceIdsPerEmployee().includes(allowance.attributes.id)
  })

  const hasAllowancesWithIncidences = !!allowancesWithIncidences.length

  const setMappingsDestroyValue = () => {
    bulkAssignEmployeeDto.mappings.forEach((mapping: AllowanceMapItem) => {
      if (mapping.newAllowanceId === 0) {
        return (mapping._destroy = true)
      }
    })
  }

  const { running: isDeleting, performAction: onSubmit } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      if (!policy) return

      if (hasAllowancesWithIncidences) {
        setIsSaving(true)
        setMappingsDestroyValue()
        await timeoffPolicies.bulkAssignEmployees(bulkAssignEmployeeDto)
      } else {
        let data: {
          timeoff_policy_id: number
          timeoff_allowance_id?: number
        } = {
          timeoff_policy_id: selectedPolicyId,
        }

        const selectedPolicy = timeoffPolicies.models.find(
          (policy) => policy.get('id') === +selectedPolicyId
        )

        if (policy.isMainPolicy && selectedPolicy) {
          await selectedPolicy.save({
            main: true,
          })
        }

        if (allowanceId) {
          data = { ...data, timeoff_allowance_id: allowanceId }
        }

        if (hasEmployees) {
          await Promise.all(employees.map((e) => e.save(data)))
        }
      }

      await policy.destroy()
      closePortal()

      navigate(navigation.settings.timeoffPolicies.fullPath())
      window.location.reload()
    }
  )

  const allAllowancesSelected = (bulkAssignEmployeeDto: BulkAssignEmployeesDto) => {
    return bulkAssignEmployeeDto.mappings.every((mapping: AllowanceMapItem) => {
      return mapping.newAllowanceId !== 0
    })
  }

  const keepAdjustments = () => {
    setShowAdjustmentLossAlert(false)
    return setShowSelectors(true)
  }

  const shouldShowAdjustmentLossAlert = () => {
    if (!allAllowancesSelected(bulkAssignEmployeeDto) && hasAllowancesWithIncidences) {
      setShowSelectors(false)
      return setShowAdjustmentLossAlert(true)
    } else {
      onSubmit(() => {})
    }
  }

  const updateBulkAssignEmployeesDto = (data: BulkAssignEmployeesDto) => {
    setBulkAssignEmployeeDto(data)
  }

  const allowanceMappings: AllowanceMapItem[] = allowancesWithIncidences.map((a) => ({
    originalAllowanceId: a.id,
    newAllowanceId: 0,
    _destroy: false,
  }))

  const [bulkAssignEmployeeDto, setBulkAssignEmployeeDto] = React.useState<BulkAssignEmployeesDto>({
    employeeIds: employeeIds,
    mappings: allowanceMappings,
    policyId: selectedPolicyId,
  } as BulkAssignEmployeesDto)

  const disabled = isDeleting || policies.length < 2 || (hasEmployees && !selectedPolicyId)

  const disableSubmit = loading

  if (!policies.length && !loading) return null

  return (
    <Modal
      illustration="holidays-event-cancel"
      title={
        showAdjustmentLossAlert
          ? i18n.t('settings.timeoff_policies.adjustment_loss_modal.title')
          : i18n.t('settings.timeoff_policies.remove_modal.title')
      }
      description={loading ? <Loading /> : description}
      onClose={onClose}
      footer={
        <>
          {showSelectors && (
            <Button disabled={disableSubmit} onClick={shouldShowAdjustmentLossAlert}>
              {i18n.t('common.submit')}
            </Button>
          )}
          {showAdjustmentLossAlert && (
            <Box flexDirection="row" alignItems="center" justifyContent="spaceEvenly" gap="s8">
              <Button type="positive" onClick={keepAdjustments} disabled={disabled}>
                {i18n.t('settings.timeoff_policies.adjustment_loss_modal.primary_button')}
              </Button>
              <Button type="negative" disabled={disabled} onClick={(event) => onSubmit(() => {})}>
                {i18n.t('settings.timeoff_policies.adjustment_loss_modal.secondary_button')}
              </Button>
            </Box>
          )}
        </>
      }
    >
      {!loading && hasEmployees && showSelectors && (
        <>
          <FormRow>
            <Select
              none={i18n.t('common.none')}
              field={policyForm.get('timeoff_policy_id')}
              label={i18n.t('settings.timeoff_policies.fields.name')}
              options={options}
              onChange={(field) => onSelect(field)}
            />
          </FormRow>
          {hasAllowancesWithIncidences && showSelectors && (
            <FormRow>
              <AllowanceMapper
                policyId={+policyForm.get('timeoff_policy_id').value}
                currentAllowances={allowancesWithIncidences}
                setBulkAssignEmployeeDto={updateBulkAssignEmployeesDto}
                bulkAssignEmployeeDto={bulkAssignEmployeeDto}
              />
            </FormRow>
          )}
        </>
      )}
    </Modal>
  )
}

export default observer(RemovePolicy)
